// ============================================================
// xFreeScan by XCloud Solutions - Executive Report Page
// Designed for CISO, CTO, CIO, CEO audiences
// ============================================================
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { scansApi } from '../../lib/api';
import AuraChat from '../../components/AuraChat';
import GraphViewer from '../../components/graphs/GraphViewer';
import VideoReport from '../../components/VideoReport';
import ValidationBadge from '../../components/ValidationBadge';
// import { usePlan } from '../../hooks/usePlan';
// import { UpgradePrompt, PlanBadge } from '../../components/UpgradePrompt';
import {
  FileText, Shield, AlertTriangle, CheckCircle,
  ChevronRight, ChevronDown, ChevronUp, ExternalLink, Printer, ArrowLeft, ArrowRight,
  Briefcase, DollarSign, Wrench, Eye, Map, Activity,
  Terminal, Server, Lock, Globe, Zap, TrendingUp, Target,
  Clock, Users, BarChart3, ShieldAlert, ShieldCheck,
  AlertCircle, Info, Radio, Bug, Network, Mail, Cpu, Play, RefreshCw
} from 'lucide-react';

// Severity config
const SEV = {
  critical: { label: 'CRITICO', bg: 'bg-red-600', text: 'text-white', light: 'bg-red-50 border-red-200 text-red-800', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 ring-red-200' },
  high:     { label: 'ALTO',    bg: 'bg-orange-500', text: 'text-white', light: 'bg-orange-50 border-orange-200 text-orange-800', dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 ring-orange-200' },
  medium:   { label: 'MEDIO',   bg: 'bg-yellow-500', text: 'text-white', light: 'bg-yellow-50 border-yellow-200 text-yellow-800', dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-700 ring-yellow-200' },
  low:      { label: 'BAJO',    bg: 'bg-blue-500', text: 'text-white', light: 'bg-blue-50 border-blue-200 text-blue-800', dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700 ring-blue-200' },
  info:     { label: 'INFO',    bg: 'bg-gray-400', text: 'text-white', light: 'bg-gray-50 border-gray-200 text-gray-600', dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600 ring-gray-200' },
} as const;

// Module icons
const MODULE_ICONS: Record<string, any> = {
  headers: Globe, ssl: Lock, dns: Mail, tech: Cpu, ports: Radio,
  web: Bug, subdomain: Network, nmap: Radio, nuclei: Bug,
  nikto: Bug, subfinder: Network, testssl: Lock,
};

export default function ReportPage() {
  const { scanId } = useParams<{ scanId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    priorities: true,
    impact: true,
    findings_critical: true,
    findings_high: true,
    findings_medium: false,
    findings_low: false,
    findings_info: false,
    methodology: false,
  });

  useEffect(() => {
    if (scanId) loadReport();
  }, [scanId]);

  const loadReport = async () => {
    try {
      const response = await scansApi.getReport(scanId!);
      if (response.success) setReport(response.data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar el reporte');
    } finally {
      setIsLoading(false);
    }
  };

  const [isPrintMode, setIsPrintMode] = useState(false);
  // 'internal' = full technical report | 'insurance' = executive for insurer
  const [printVariant, setPrintVariant] = useState<'internal' | 'insurance'>('internal');
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const executePrint = (variant: 'internal' | 'insurance') => {
    setPrintVariant(variant);
    setIsPrintMode(true);
    setShowPrintMenu(false);
    setExpandedSections(prev => {
      const all: Record<string, boolean> = {};
      for (const k of Object.keys(prev)) all[k] = true;
      all['priorities'] = true;
      all['impact'] = true;
      all['findings_critical'] = true;
      all['findings_high'] = true;
      all['findings_medium'] = true;
      all['findings_low'] = true;
      all['findings_info'] = true;
      all['methodology'] = true;
      return all;
    });

    setTimeout(() => {
      const overrides: { el: HTMLElement; orig: string }[] = [];
      const forceStyle = (selector: string, css: string) => {
        document.querySelectorAll<HTMLElement>(selector).forEach(el => {
          overrides.push({ el, orig: el.getAttribute('style') || '' });
          el.style.cssText += `;${css}`;
        });
      };

      forceStyle('[class*="h-screen"]', 'height:auto!important;overflow:visible!important;display:block!important');
      forceStyle('[class*="overflow-hidden"]', 'overflow:visible!important;height:auto!important');
      forceStyle('[class*="overflow-y-auto"]', 'overflow:visible!important;height:auto!important');
      forceStyle('.flex-1', 'flex:none!important;height:auto!important;overflow:visible!important');
      forceStyle('main', 'overflow:visible!important;height:auto!important;flex:none!important');
      forceStyle('[class*="w-64"]', 'display:none!important');
      forceStyle('[class*="w-16"]', 'display:none!important');
      forceStyle('aside', 'display:none!important');
      forceStyle('nav', 'display:none!important');

      // Hide sections based on variant
      if (variant === 'insurance') {
        forceStyle('[data-section="internal-only"]', 'display:none!important');
      } else {
        forceStyle('[data-section="insurance-only"]', 'display:none!important');
      }

      void document.body.offsetHeight;
      window.print();

      overrides.forEach(({ el, orig }) => {
        if (orig) el.setAttribute('style', orig);
        else el.removeAttribute('style');
      });

      setIsPrintMode(false);
    }, 500);
  };
  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-purple-600 border-t-transparent mx-auto" />
          <p className="mt-4 text-sm text-gray-500">Cargando informe ejecutivo...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="text-center py-20">
        <AlertTriangle className="mx-auto h-12 w-12 text-orange-500" />
        <p className="mt-4 text-gray-500">{error || 'Reporte no disponible'}</p>
        <p className="mt-2 text-sm text-gray-400">El escaneo podria no haber terminado aun. Intentelo de nuevo en unos momentos.</p>
        <Link to={`/app/scans/${scanId}`} className="btn-secondary mt-4 inline-flex">
          <ArrowLeft className="h-4 w-4" /> Volver al Escaneo
        </Link>
      </div>
    );
  }

  const summary = report.summary;
  const recData = report.recommendations || {};
  const recommendations = recData.services || [];
  const benchmark = recData.benchmark || null;
  const riskMatrix = recData.risk_matrix || null;
  const methodology = recData.methodology || null;
  const roadmap = recData.roadmap || null;
  const businessImpact = recData.business_impact || null;
  const topPriorities = recData.top_priorities || [];
  const comparison = recData.comparison || null;
  const allFindings = report.findings || [];
  const nistMaturity = recData.nist_maturity || null;
  const cyberRisk = recData.cyber_risk || null;
  const insuranceCert = recData.insurance_certificate || null;
  const attackChains = recData.attack_chains || [];

  const gradeColor = (g: string) =>
    g?.startsWith('A') ? 'from-emerald-500 to-emerald-600' :
    g?.startsWith('B') ? 'from-green-500 to-green-600' :
    g?.startsWith('C') ? 'from-yellow-500 to-yellow-600' :
    g?.startsWith('D') ? 'from-orange-500 to-orange-600' : 'from-red-500 to-red-600';

  const riskBgColor = (risk: string) =>
    risk === 'CRITICO' ? 'from-red-600 to-red-700' :
    risk === 'ALTO' ? 'from-orange-500 to-orange-600' :
    risk === 'MEDIO' ? 'from-yellow-500 to-yellow-600' : 'from-emerald-500 to-emerald-600';

  const riskIcon = (risk: string) =>
    risk === 'CRITICO' || risk === 'ALTO' ? ShieldAlert : risk === 'MEDIO' ? Shield : ShieldCheck;

  const RiskIconComponent = businessImpact ? riskIcon(businessImpact.overall_risk) : Shield;

  return (
    <div className="mx-auto max-w-5xl space-y-6 print:max-w-none print:space-y-4">
      {/* ========== NAVIGATION BAR ========== */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <Link to={`/app/scans/${scanId}`} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Informe Ejecutivo de Seguridad</h1>
        </div>
        <div className="flex items-center gap-2">
          {false && (
            <button onClick={() => {}} className="btn-secondary text-sm border-amber-300 text-amber-700 hover:bg-amber-50">
              <Zap className="h-4 w-4" />
              Desbloquear Reporte Completo
            </button>
          )}
          <button
              onClick={() => navigate(`/app/scans/new?target=${encodeURIComponent(report.scan?.target || report.title?.replace('Informe de Seguridad - ', '') || '')}`)}
              className="btn-secondary text-sm"
            >
              <RefreshCw className="h-4 w-4" /> Re-escanear
            </button>
          <button onClick={() => setShowVideo(true)} className="btn-secondary text-sm">
              <Play className="h-4 w-4" />
              Video Resumen
            </button>
          <div className="relative">
            <button onClick={() => setShowPrintMenu(!showPrintMenu)} className="btn-secondary text-sm">
              <Printer className="h-4 w-4" />
              Generar PDF
              <ChevronDown className="h-3 w-3" />
            </button>
            {showPrintMenu && (
              <div className="absolute right-0 mt-2 w-72 rounded-xl border border-gray-200 bg-white shadow-xl z-50">
                <button onClick={() => executePrint('internal')}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-t-xl border-b border-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100">
                      <Terminal className="h-4 w-4 text-purple-700" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Informe Interno</p>
                      <p className="text-xs text-gray-500">Tecnico completo: hallazgos, evidencia, metodologia, remediacion</p>
                    </div>
                  </div>
                </button>
                <button onClick={() => executePrint('insurance')}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 rounded-b-xl transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100">
                      <FileText className="h-4 w-4 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Informe para Poliza</p>
                      <p className="text-xs text-gray-500">Ejecutivo: madurez NIST, riesgo financiero, certificado, benchmark</p>
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pro findings banner */}
      {report.pro_findings && report.pro_findings.count > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-5 py-4 print:hidden">
          <div className="flex-shrink-0 bg-amber-500 rounded-full p-2">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-amber-900">
              {report.pro_findings.count} hallazgos adicionales encontrados
            </p>
            <p className="text-sm text-amber-700">
              Los modulos de escaneo profundo y pentesting activo detectaron vulnerabilidades adicionales no incluidas en este reporte.
            </p>
          </div>
          <button onClick={() => {}} className="flex-shrink-0 inline-flex items-center gap-1 px-4 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 text-sm">
            <Zap className="h-4 w-4" /> Ver con Pro
          </button>
        </div>
      )}

      {/* Report type indicator */}
      {false && (
        <div className="text-center text-xs text-gray-400 print:hidden">
          Reporte Basico — <button onClick={() => {}} className="text-amber-500 hover:text-amber-400 underline">Actualiza a Pro</button> para reporte ejecutivo completo con evidencia, impacto de negocio y PoC
        </div>
      )}

      {/* ========== PRINT: PROFESSIONAL COVER PAGE ========== */}
      <div className="hidden print:flex print-cover flex-col items-center justify-center text-center">
        <div className="mb-8">
          <div className="mx-auto h-20 w-20 rounded-2xl bg-purple-700 flex items-center justify-center shadow-lg">
            <Shield className="h-10 w-10 text-white" />
          </div>
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">
          {isPrintMode && printVariant === 'insurance' ? 'CERTIFICADO DE CIBERSEGURIDAD' : 'INFORME DE SEGURIDAD'}
        </h1>
        <h2 className="text-2xl font-bold text-purple-700 mt-2">
          {isPrintMode && printVariant === 'insurance' ? 'Evaluacion para Poliza de Ciberseguro' : 'Evaluacion de Vulnerabilidades'}
        </h2>
        <div className="mt-8 w-24 h-1 bg-purple-600 rounded-full mx-auto" />
        <div className="mt-8 space-y-2">
          <p className="text-xl font-bold text-gray-800">{report.scan?.target || report.title?.replace('Informe de Seguridad - ', '')}</p>
          <p className="text-gray-500">
            {new Date(report.generated_at).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        {summary && (
          <div className="mt-10 flex items-center gap-6 justify-center">
            <div className="text-center">
              <div className={`inline-flex h-16 w-16 items-center justify-center rounded-xl bg-purple-700 text-white text-2xl font-black`}>
                {summary.grade}
              </div>
              <p className="text-xs text-gray-500 mt-2">Calificacion</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-gray-900">{summary.score}<span className="text-lg text-gray-400">/100</span></p>
              <p className="text-xs text-gray-500">Score</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-black text-gray-900">{summary.total_findings}</p>
              <p className="text-xs text-gray-500">Hallazgos</p>
            </div>
            {businessImpact && (
              <div className="text-center">
                <p className={`text-lg font-black ${businessImpact.overall_risk === 'CRITICO' ? 'text-red-600' : businessImpact.overall_risk === 'ALTO' ? 'text-orange-600' : businessImpact.overall_risk === 'MEDIO' ? 'text-yellow-600' : 'text-emerald-600'}`}>
                  {businessImpact.overall_risk}
                </p>
                <p className="text-xs text-gray-500">Nivel de Riesgo</p>
              </div>
            )}
          </div>
        )}
        <div className="mt-auto pt-12 text-gray-400 text-sm space-y-1">
          <p className="font-bold text-gray-600">CONFIDENCIAL</p>
          <p>Generado por xFreeScan v3.0 — XCloud Solutions</p>
          <p>WhatsApp: +57 316 565 0842 | info@xclos.com | www.xclos.com</p>
        </div>
      </div>

      {/* ========== PRINT: TABLE OF CONTENTS ========== */}
      <div className="hidden print:block print-toc">
        <h2 className="text-2xl font-black text-gray-900 mb-6 border-b-2 border-purple-600 pb-2">Tabla de Contenidos</h2>
        <div className="space-y-1">
          <div className="print-toc-item"><span className="font-medium">1. Resumen Ejecutivo y Calificacion</span></div>
          {comparison?.has_previous && <div className="print-toc-item"><span className="font-medium">2. Progreso vs Escaneo Anterior</span></div>}
          <div className="print-toc-item"><span className="font-medium">{comparison?.has_previous ? '3' : '2'}. Mensaje para la Direccion</span></div>
          <div className="print-toc-item"><span className="font-medium">{comparison?.has_previous ? '4' : '3'}. Top Prioridades de Remediacion</span></div>
          {businessImpact?.risks?.length > 0 && <div className="print-toc-item"><span className="font-medium">{comparison?.has_previous ? '5' : '4'}. Impacto al Negocio</span></div>}
          <div className="print-toc-item"><span className="font-medium">{comparison?.has_previous ? '6' : '5'}. Benchmark — Posicion en la Industria</span></div>
          <div className="print-toc-item"><span className="font-medium">{comparison?.has_previous ? '7' : '6'}. Distribucion de Hallazgos</span></div>
          {roadmap && <div className="print-toc-item"><span className="font-medium">{comparison?.has_previous ? '8' : '7'}. Plan de Accion por Fases</span></div>}
          <div className="print-toc-item"><span className="font-bold">Hallazgos Detallados</span></div>
          {allFindings.filter((f: any) => f.severity === 'critical').length > 0 && <div className="print-toc-item pl-4"><span>— Criticos ({allFindings.filter((f: any) => f.severity === 'critical').length})</span></div>}
          {allFindings.filter((f: any) => f.severity === 'high').length > 0 && <div className="print-toc-item pl-4"><span>— Altos ({allFindings.filter((f: any) => f.severity === 'high').length})</span></div>}
          {allFindings.filter((f: any) => f.severity === 'medium').length > 0 && <div className="print-toc-item pl-4"><span>— Medios ({allFindings.filter((f: any) => f.severity === 'medium').length})</span></div>}
          {allFindings.filter((f: any) => f.severity === 'low').length > 0 && <div className="print-toc-item pl-4"><span>— Bajos ({allFindings.filter((f: any) => f.severity === 'low').length})</span></div>}
          {allFindings.filter((f: any) => f.severity === 'info').length > 0 && <div className="print-toc-item pl-4"><span>— Informativos ({allFindings.filter((f: any) => f.severity === 'info').length})</span></div>}
          <div className="print-toc-item"><span className="font-medium">Metodologia de Escaneo</span></div>
          <div className="print-toc-item"><span className="font-medium">Servicios Profesionales Recomendados</span></div>
        </div>
        <div className="mt-8 text-xs text-gray-400">
          <p>Total de hallazgos: {allFindings.length} | Modulos ejecutados: {methodology?.total_modules || 'N/A'}</p>
          <p>Este informe es confidencial y esta dirigido exclusivamente al destinatario.</p>
        </div>
      </div>

      {/* ========== 1. COVER / HERO SECTION ========== */}
      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg print:shadow-none">
        {/* Risk level banner */}
        {businessImpact && (
          <div className={`bg-gradient-to-r ${riskBgColor(businessImpact.overall_risk)} px-6 py-4 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RiskIconComponent className="h-7 w-7" />
                <div>
                  <p className="text-xs font-medium uppercase opacity-80">Nivel de Riesgo Organizacional</p>
                  <p className="text-2xl font-black tracking-wide">{businessImpact.overall_risk}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs opacity-80">Ventana de Exposicion Recomendada</p>
                <p className="text-lg font-bold">{riskMatrix?.estimated_exposure_window || 'N/A'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main header */}
        <div className="bg-white p-6">
          <div className="flex items-start gap-6">
            <div className={`flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br ${gradeColor(summary?.grade)} shadow-lg flex-shrink-0`}>
              <span className="text-3xl font-black text-white">{summary?.grade || '?'}</span>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">{report.title}</h2>
              <p className="mt-1 text-sm text-gray-500">
                {new Date(report.generated_at).toLocaleDateString('es', {
                  year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                })}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">xFreeScan by XCloud Solutions | www.xclos.com</p>
            </div>
          </div>

          {/* Score dashboard row */}
          {summary && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              <ScoreCard label="Score" value={summary.score} suffix="/100" color="text-gray-900" />
              <ScoreCard label="Calificacion" value={summary.grade} color={
                summary.grade?.startsWith('A') ? 'text-emerald-600' :
                summary.grade?.startsWith('B') ? 'text-green-600' :
                summary.grade?.startsWith('C') ? 'text-yellow-600' :
                summary.grade?.startsWith('D') ? 'text-orange-600' : 'text-red-600'
              } />
              <ScoreCard label="Criticos" value={summary.critical} color="text-red-600" highlight={summary.critical > 0} />
              <ScoreCard label="Altos" value={summary.high} color="text-orange-500" highlight={summary.high > 0} />
              <ScoreCard label="Medios" value={summary.medium} color="text-yellow-600" />
              <ScoreCard label="Bajos" value={summary.low} color="text-blue-500" />
              <ScoreCard label="Percentil" value={benchmark?.percentile || '—'} suffix="%" color="text-purple-600" />
            </div>
          )}
        </div>
      </div>

      {/* ========== 1b. COMPARISON vs PREVIOUS SCAN ========== */}
      {comparison?.has_previous && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-purple-500" />
            Progreso vs Escaneo Anterior
            <span className="text-sm font-normal text-gray-500">
              ({comparison.days_between} dias desde el {new Date(comparison.previous_date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })})
            </span>
          </h3>

          {/* Score comparison banner */}
          <div className={`rounded-xl p-4 mb-4 ${
            comparison.score.delta > 0 ? 'bg-emerald-50 border border-emerald-200' :
            comparison.score.delta < 0 ? 'bg-red-50 border border-red-200' :
            'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                {/* Previous */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Anterior</p>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${gradeColor(comparison.grade.previous)} text-white font-black text-xl`}>
                    {comparison.grade.previous}
                  </div>
                  <p className="text-lg font-bold text-gray-700 mt-1">{comparison.score.previous}/100</p>
                </div>

                {/* Arrow */}
                <div className="flex flex-col items-center">
                  <ArrowRight className={`h-6 w-6 ${comparison.score.delta > 0 ? 'text-emerald-500' : comparison.score.delta < 0 ? 'text-red-500' : 'text-gray-400'}`} />
                </div>

                {/* Current */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Actual</p>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${gradeColor(summary?.grade)} text-white font-black text-xl`}>
                    {summary?.grade}
                  </div>
                  <p className="text-lg font-bold text-gray-700 mt-1">{summary?.score}/100</p>
                </div>

                {/* Delta badge */}
                <div className={`px-4 py-2 rounded-lg ${
                  comparison.score.delta > 0 ? 'bg-emerald-100 text-emerald-800' :
                  comparison.score.delta < 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  <p className="text-2xl font-black">
                    {comparison.score.delta > 0 ? '+' : ''}{comparison.score.delta}
                  </p>
                  <p className="text-xs font-medium">puntos</p>
                </div>
              </div>
            </div>

            {comparison.narrative && (
              <p className="mt-3 text-sm text-gray-700">{comparison.narrative}</p>
            )}
          </div>

          {/* Finding status: resolved / new / persistent */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
              <CheckCircle className="h-5 w-5 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-700">{comparison.findings.resolved_count}</p>
              <p className="text-xs text-emerald-600 font-medium">Resueltos</p>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
              <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-amber-700">{comparison.findings.new_count}</p>
              <p className="text-xs text-amber-600 font-medium">Nuevos</p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-center">
              <ArrowRight className="h-5 w-5 text-gray-400 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-600">{comparison.findings.persistent_count}</p>
              <p className="text-xs text-gray-500 font-medium">Persisten</p>
            </div>
          </div>

          {/* Severity delta bars */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            {(['critical', 'high', 'medium', 'low'] as const).map(sev => {
              const d = comparison.severity[sev];
              if (!d) return null;
              const sevConf = SEV[sev];
              return (
                <div key={sev} className="text-center">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${sevConf.badge} ring-1`}>
                    {sevConf.label}
                  </span>
                  <p className="text-sm mt-1">
                    <span className="text-gray-400">{d.previous}</span>
                    <span className="mx-1 text-gray-300">&rarr;</span>
                    <span className="font-bold text-gray-700">{d.current}</span>
                    {d.delta !== 0 && (
                      <span className={`ml-1 text-xs font-bold ${d.delta < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ({d.delta > 0 ? '+' : ''}{d.delta})
                      </span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Resolved findings list (collapsed) */}
          {comparison.findings.resolved_count > 0 && (
            <details className="mt-4">
              <summary className="text-sm font-medium text-emerald-700 cursor-pointer hover:underline">
                Ver {comparison.findings.resolved_count} vulnerabilidades resueltas
              </summary>
              <ul className="mt-2 space-y-1">
                {comparison.findings.resolved.map((title: string, i: number) => (
                  <li key={i} className="text-xs text-emerald-700 flex items-start gap-1.5">
                    <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {title}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* New findings list (collapsed) */}
          {comparison.findings.new_count > 0 && (
            <details className="mt-3">
              <summary className="text-sm font-medium text-amber-700 cursor-pointer hover:underline">
                Ver {comparison.findings.new_count} nuevas vulnerabilidades
              </summary>
              <ul className="mt-2 space-y-1">
                {comparison.findings.new_findings.map((title: string, i: number) => (
                  <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {title}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* ========== 2. CEO MESSAGE (plain language) ========== */}
      {businessImpact && (
        <div className="card border-l-4 border-l-purple-600">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 flex-shrink-0">
              <Users className="h-5 w-5 text-purple-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Para la Direccion</h3>
              <p className="mt-2 text-gray-700 leading-relaxed">{businessImpact.ceo_message}</p>
            </div>
          </div>
        </div>
      )}

      {/* ========== 3. TOP PRIORITIES (where to start) ========== */}
      {topPriorities.length > 0 && (
        <div className="card">
          <button onClick={() => toggleSection('priorities')} className="w-full flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Target className="h-5 w-5 text-red-500" />
              Donde Empezar: Top {topPriorities.length} Prioridades
            </h3>
            {expandedSections.priorities ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>

          {expandedSections.priorities && (
            <div className="mt-4 space-y-4">
              {topPriorities.map((p: any) => {
                const sev = SEV[p.severity as keyof typeof SEV] || SEV.medium;
                return (
                  <div key={p.rank} className={`rounded-xl border p-4 ${sev.light}`}>
                    <div className="flex items-start gap-4">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-full ${sev.bg} ${sev.text} text-sm font-black flex-shrink-0`}>
                        {p.rank}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-gray-900">{p.title}</h4>
                          <span className={`badge ring-1 ${sev.badge}`}>{sev.label}</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" /> {p.timeline}
                          </span>
                        </div>
                        <div className="mt-2 p-3 rounded-lg bg-white/60 border border-white/80">
                          <p className="text-sm font-semibold text-gray-800 mb-1">Por que es importante:</p>
                          <p className="text-sm text-gray-700">{p.why}</p>
                        </div>
                        <div className="mt-2 p-3 rounded-lg bg-emerald-50/60 border border-emerald-200/60">
                          <p className="text-sm font-semibold text-emerald-800 mb-1">Que hacer:</p>
                          <p className="text-sm text-emerald-700">{p.what}</p>
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Wrench className="h-3 w-3" /> Esfuerzo: {p.effort}</span>
                          <span className="flex items-center gap-1"><Cpu className="h-3 w-3" /> Modulo: {p.module}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========== 4. BUSINESS IMPACT (risk to the organization) ========== */}
      {businessImpact?.risks?.length > 0 && (
        <div className={`card relative ${false ? 'overflow-hidden' : ''}`}>
          {false && (
            <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl cursor-pointer" onClick={() => {}}>
              <Lock className="h-8 w-8 text-amber-500 mb-2" />
              <p className="font-semibold text-gray-800">Impacto al Negocio</p>
              <p className="text-sm text-gray-500 mt-1">Analisis de costos, riesgos regulatorios y reputacionales</p>
              <span className="mt-3 inline-flex items-center gap-1 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg">
                <Zap className="h-4 w-4" /> Disponible con Pro
              </span>
            </div>
          )}
          <button onClick={() => toggleSection('impact')} className="w-full flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Impacto al Negocio
            </h3>
            {expandedSections.impact ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>

          {expandedSections.impact && (
            <div className="mt-4 space-y-3">
              {businessImpact.risks.map((risk: any, idx: number) => {
                const sev = SEV[risk.level as keyof typeof SEV] || SEV.medium;
                return (
                  <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 h-3 w-3 rounded-full flex-shrink-0 ${sev.dot}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-gray-900">{risk.category}</h4>
                          <span className={`badge ring-1 text-xs ${sev.badge}`}>{sev.label}</span>
                        </div>
                        <p className="mt-1 text-sm text-gray-700">{risk.description}</p>
                        <div className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                          <p className="text-xs font-semibold text-amber-800 mb-0.5">Consecuencia potencial:</p>
                          <p className="text-xs text-amber-700">{risk.consequence}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Breach cost reference */}
              <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                  <BarChart3 className="h-3.5 w-3.5" /> Referencia de costos (IBM Cost of Data Breach Report 2024)
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">$4.88M</p>
                    <p className="text-[10px] text-gray-500">Costo prom. global brecha</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">$2.76M</p>
                    <p className="text-[10px] text-gray-500">Costo prom. LATAM</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">204 dias</p>
                    <p className="text-[10px] text-gray-500">Tiempo prom. deteccion</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">$165</p>
                    <p className="text-[10px] text-gray-500">Costo por registro robado</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== 5. BENCHMARK COMPARISON ========== */}
      {benchmark && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-purple-600" />
            Su Posicion en la Industria
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 rounded-xl bg-purple-50 border border-purple-200">
              <p className="text-3xl font-black text-purple-700">{benchmark.your_score}</p>
              <p className="text-xs text-purple-600 mt-1 font-medium">Su Score</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-gray-50 border border-gray-200">
              <p className="text-3xl font-black text-gray-600">{benchmark.industry_avg}</p>
              <p className="text-xs text-gray-500 mt-1">Promedio Industria</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-emerald-50 border border-emerald-200">
              <p className="text-3xl font-black text-emerald-600">{benchmark.top_quartile}</p>
              <p className="text-xs text-emerald-600 mt-1">Top 25%</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-orange-50 border border-orange-200">
              <p className="text-3xl font-black text-orange-500">{benchmark.gap_to_top > 0 ? `+${benchmark.gap_to_top}` : '0'}</p>
              <p className="text-xs text-orange-500 mt-1">Puntos para Top 25%</p>
            </div>
          </div>
          {/* Visual bar */}
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>0</span>
              <span className="text-gray-500">Promedio ({benchmark.industry_avg})</span>
              <span className="text-emerald-600 font-medium">Top 25% ({benchmark.top_quartile})</span>
              <span>100</span>
            </div>
            <div className="relative h-5 rounded-full bg-gray-200 overflow-hidden">
              <div className="absolute h-full bg-gradient-to-r from-red-500 via-yellow-400 to-emerald-500 rounded-full transition-all"
                   style={{ width: `${Math.min(100, benchmark.your_score)}%` }} />
              <div className="absolute h-full border-l-2 border-dashed border-gray-600 opacity-60"
                   style={{ left: `${benchmark.industry_avg}%` }} />
              <div className="absolute h-full border-l-2 border-dashed border-emerald-700"
                   style={{ left: `${benchmark.top_quartile}%` }} />
            </div>
            <p className="text-xs text-center text-gray-500 mt-2">
              {benchmark.position === 'below_average' && `Su organizacion esta en el percentil ${benchmark.percentile} — por debajo del promedio de la industria.`}
              {benchmark.position === 'average' && `Su organizacion esta en el percentil ${benchmark.percentile} — en el promedio de la industria.`}
              {benchmark.position === 'above_average' && `Su organizacion esta en el percentil ${benchmark.percentile} — por encima del promedio. Excelente postura.`}
            </p>
          </div>
        </div>
      )}

      {/* ========== 6. RISK MATRIX ========== */}
      {riskMatrix && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Distribucion de Hallazgos
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
            {(['critical', 'high', 'medium', 'low', 'info'] as const).map((sev) => {
              const config = SEV[sev];
              const count = riskMatrix.distribution?.[sev]?.count ?? 0;
              return (
                <div key={sev} className={`text-center p-3 rounded-xl border ${config.light}`}>
                  <p className="text-3xl font-black">{count}</p>
                  <p className="text-xs mt-1 font-medium">{config.label}</p>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-sm p-3 rounded-lg bg-gray-50 border border-gray-200">
            <span className="text-gray-600">
              <strong>{riskMatrix.total_actionable}</strong> hallazgos que requieren accion
            </span>
            <span className={`font-bold ${
              riskMatrix.estimated_exposure_window === 'menos de 48 horas' ? 'text-red-600' :
              riskMatrix.estimated_exposure_window === '1-2 semanas' ? 'text-orange-600' :
              riskMatrix.estimated_exposure_window === '1-3 meses' ? 'text-yellow-600' : 'text-emerald-600'
            }`}>
              Remediar en: {riskMatrix.estimated_exposure_window}
            </span>
          </div>
        </div>
      )}

      <div className="print-page-break-before" />

      {/* ========== NIST CSF 2.0 MATURITY ========== */}
      {nistMaturity && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Madurez NIST CSF 2.0
            <span className={`ml-2 px-3 py-1 rounded-full text-sm font-black ${
              nistMaturity.overall_tier >= 4 ? 'bg-emerald-100 text-emerald-800' :
              nistMaturity.overall_tier === 3 ? 'bg-green-100 text-green-800' :
              nistMaturity.overall_tier === 2 ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              Tier {nistMaturity.overall_tier}: {nistMaturity.overall_tier_label}
            </span>
          </h3>

          {/* Overall score bar */}
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Score General de Madurez</span>
              <span className="text-2xl font-black text-gray-900">{nistMaturity.overall_score}/100</span>
            </div>
            <div className="h-4 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all"
                   style={{ width: `${nistMaturity.overall_score}%` }} />
            </div>
            <p className="mt-2 text-xs text-gray-500">Cobertura de evaluacion: {nistMaturity.coverage_percentage}% de las funciones NIST</p>
          </div>

          {/* 6 function cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {Object.entries(nistMaturity.functions).map(([key, fn]: [string, any]) => (
              <div key={key} className={`text-center p-3 rounded-xl border ${
                fn.tier >= 4 ? 'bg-emerald-50 border-emerald-200' :
                fn.tier === 3 ? 'bg-green-50 border-green-200' :
                fn.tier === 2 ? 'bg-yellow-50 border-yellow-200' :
                'bg-red-50 border-red-200'
              }`}>
                <p className="text-xs font-bold text-gray-500 uppercase">{key}</p>
                <p className="text-sm font-bold text-gray-800 mt-1">{fn.name}</p>
                <p className="text-2xl font-black mt-1">{fn.score}</p>
                <p className={`text-[10px] font-bold mt-1 ${
                  fn.tier >= 4 ? 'text-emerald-700' :
                  fn.tier === 3 ? 'text-green-700' :
                  fn.tier === 2 ? 'text-yellow-700' :
                  'text-red-700'
                }`}>Tier {fn.tier}: {fn.tier_label}</p>
                {fn.critical_count > 0 && (
                  <p className="text-[10px] text-red-600 font-bold mt-1">{fn.critical_count} criticos</p>
                )}
              </div>
            ))}
          </div>

          {/* Gaps */}
          {Object.entries(nistMaturity.functions).some(([, fn]: [string, any]) => fn.gaps?.length > 0) && (
            <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm font-bold text-amber-800 mb-2">Brechas Identificadas</p>
              <div className="space-y-2">
                {Object.entries(nistMaturity.functions).map(([key, fn]: [string, any]) =>
                  fn.gaps?.map((gap: string, i: number) => (
                    <div key={`${key}-${i}`} className="flex items-start gap-2 text-sm">
                      <span className="font-bold text-amber-700 flex-shrink-0">{key}:</span>
                      <span className="text-amber-700">{gap}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Recommendation */}
          <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
            <p className="text-sm font-bold text-blue-800 mb-1">Recomendacion para Aseguradora</p>
            <p className="text-sm text-blue-700">{nistMaturity.recommendation}</p>
          </div>
        </div>
      )}

      {/* ========== CYBER RISK QUANTIFICATION ========== */}
      {cyberRisk && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Cuantificacion de Riesgo Cibernetico (CRQ)
            <span className={`ml-2 px-3 py-1 rounded-full text-sm font-black ${
              cyberRisk.risk_rating === 'CRITICAL' ? 'bg-red-100 text-red-800' :
              cyberRisk.risk_rating === 'HIGH' ? 'bg-orange-100 text-orange-800' :
              cyberRisk.risk_rating === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
              'bg-emerald-100 text-emerald-800'
            }`}>
              Riesgo: {cyberRisk.risk_rating}
            </span>
          </h3>

          {/* Key financial metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 rounded-xl bg-red-50 border border-red-200">
              <p className="text-2xl font-black text-red-700">{Math.round(cyberRisk.breach_probability_12m * 100)}%</p>
              <p className="text-[10px] text-red-600 font-medium mt-1">Prob. Brecha 12 meses</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-orange-50 border border-orange-200">
              <p className="text-2xl font-black text-orange-700">${(cyberRisk.estimated_breach_cost_usd / 1000000).toFixed(1)}M</p>
              <p className="text-[10px] text-orange-600 font-medium mt-1">Costo Estimado Brecha</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-purple-50 border border-purple-200">
              <p className="text-2xl font-black text-purple-700">${(cyberRisk.annual_loss_expectancy_usd / 1000).toFixed(0)}K</p>
              <p className="text-[10px] text-purple-600 font-medium mt-1">Perdida Anual Esperada</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-green-50 border border-green-200">
              <p className="text-lg font-black text-green-700">${(cyberRisk.premium_estimate_range.min / 1000).toFixed(0)}K - ${(cyberRisk.premium_estimate_range.max / 1000).toFixed(0)}K</p>
              <p className="text-[10px] text-green-600 font-medium mt-1">Prima Estimada Anual</p>
            </div>
          </div>

          {/* Risk factors */}
          {cyberRisk.risk_factors?.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-bold text-gray-700 mb-2">Factores de Riesgo</p>
              <div className="space-y-2">
                {cyberRisk.risk_factors.map((rf: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 text-sm p-2.5 rounded-lg border border-gray-200">
                    <span className={`mt-0.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                      rf.impact === 'high' ? 'bg-red-500' : rf.impact === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                    }`} />
                    <div>
                      <span className="font-medium text-gray-800">{rf.factor}</span>
                      <p className="text-xs text-gray-500">{rf.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ROI of remediation */}
          {cyberRisk.roi_remediation && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <p className="text-sm font-bold text-emerald-800 mb-2">ROI de Remediacion</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-black text-gray-900">${(cyberRisk.roi_remediation.cost_to_fix / 1000).toFixed(0)}K</p>
                  <p className="text-[10px] text-gray-500">Costo Remediacion</p>
                </div>
                <div>
                  <p className="text-lg font-black text-emerald-700">${(cyberRisk.roi_remediation.potential_savings / 1000).toFixed(0)}K</p>
                  <p className="text-[10px] text-gray-500">Ahorro Potencial</p>
                </div>
                <div>
                  <p className="text-lg font-black text-emerald-700">{cyberRisk.roi_remediation.roi_percentage}%</p>
                  <p className="text-[10px] text-gray-500">ROI</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== INSURANCE CERTIFICATE — Insurance report only ========== */}
      <div data-section="insurance-only">
      {insuranceCert && (
        <div className="card border-2 border-purple-300 bg-gradient-to-br from-white to-purple-50/30">
          <div className="text-center mb-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-purple-700 text-white mb-2">
              <FileText className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Certificado de Evaluacion de Ciberseguridad</h3>
            <p className="text-xs text-gray-500 font-mono mt-1">{insuranceCert.certificate_id}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="text-center p-3 rounded-xl bg-white border border-gray-200">
              <p className="text-xs text-gray-500">Objetivo</p>
              <p className="text-sm font-bold text-gray-900 break-all">{insuranceCert.target}</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-white border border-gray-200">
              <p className="text-xs text-gray-500">Fecha Evaluacion</p>
              <p className="text-sm font-bold text-gray-900">{insuranceCert.scan_date}</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-white border border-gray-200">
              <p className="text-xs text-gray-500">Valido Hasta</p>
              <p className="text-sm font-bold text-orange-600">{insuranceCert.valid_until}</p>
            </div>
            <div className="text-center p-3 rounded-xl bg-white border border-gray-200">
              <p className="text-xs text-gray-500">Nivel NIST</p>
              <p className="text-sm font-bold text-purple-700">Tier {insuranceCert.nist_tier}: {insuranceCert.nist_tier_label}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <span className="text-xs text-gray-500 font-medium">Frameworks evaluados:</span>
            {insuranceCert.frameworks_assessed?.map((fw: string, i: number) => (
              <span key={i} className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{fw}</span>
            ))}
          </div>

          <p className="text-[10px] text-gray-400 text-center italic">{insuranceCert.disclaimer}</p>
        </div>
      )}
      </div>{/* end insurance-only certificate wrapper */}

      {/* ========== GRAPH VIEWER — 5 interactive graph views ========== */}
      {allFindings.filter((f: any) => f.severity !== 'info').length > 0 && (
        <GraphViewer
          findings={allFindings}
          attackChains={attackChains}
          target={report.scan?.target || report.title?.replace('Informe de Seguridad - ', '') || ''}
        />
      )}

      {/* ========== ATTACK CHAINS ========== */}
      {attackChains.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="h-5 w-5 text-red-500" />
            Cadenas de Ataque Identificadas ({attackChains.length})
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Estos son caminos de ataque multi-paso construidos a partir de los hallazgos reales del escaneo.
          </p>
          <div className="space-y-4">
            {attackChains.map((chain: any, ci: number) => (
              <div key={ci} className={`rounded-xl border p-4 ${
                chain.severity === 'critical' ? 'border-red-300 bg-red-50' :
                chain.severity === 'high' ? 'border-orange-300 bg-orange-50' :
                'border-yellow-300 bg-yellow-50'
              }`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${
                    chain.severity === 'critical' ? 'bg-red-600' : chain.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                  }`}>{chain.severity?.toUpperCase()}</span>
                  <h4 className="font-bold text-gray-900">{chain.title}</h4>
                </div>

                {/* Steps as visual flow */}
                <div className="flex flex-wrap items-center gap-1 mb-3">
                  {chain.steps?.map((step: any, si: number) => (
                    <div key={si} className="flex items-center gap-1">
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs">
                        <span className="font-bold text-purple-700">{step.step}</span>
                        <span className="text-gray-700">{step.finding?.substring(0, 40)}{step.finding?.length > 40 ? '...' : ''}</span>
                      </div>
                      {si < (chain.steps?.length || 0) - 1 && <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                    </div>
                  ))}
                </div>

                {/* Impact */}
                {chain.business_impact && (
                  <div className="p-3 rounded-lg bg-white/70 border border-gray-200">
                    <p className="text-xs font-bold text-gray-600 mb-1">Impacto de Negocio</p>
                    <p className="text-sm text-gray-700">{chain.business_impact}</p>
                    {chain.data_at_risk && (
                      <p className="text-xs text-red-600 font-medium mt-1">Datos en riesgo: {chain.data_at_risk}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== 7. ATTACK SURFACE & ROADMAP ========== */}
      {roadmap && roadmap.phases?.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Map className="h-5 w-5 text-emerald-600" />
            Plan de Accion por Fases
          </h3>

          {/* Score improvement projection */}
          {roadmap.score_projection && (
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-emerald-50 border border-gray-200">
              <p className="text-sm font-bold text-gray-700 mb-3">Proyeccion de mejora del Score:</p>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-center">
                  <p className="text-2xl font-black text-red-600">{roadmap.score_projection.current}</p>
                  <p className="text-[10px] text-gray-500">Actual</p>
                </div>
                {roadmap.score_projection.current !== roadmap.score_projection.after_critical && (
                  <>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div className="text-center">
                      <p className="text-2xl font-black text-orange-500">{roadmap.score_projection.after_critical}</p>
                      <p className="text-[10px] text-gray-500">Fase 1</p>
                    </div>
                  </>
                )}
                {roadmap.score_projection.after_critical !== roadmap.score_projection.after_high && (
                  <>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                    <div className="text-center">
                      <p className="text-2xl font-black text-yellow-600">{roadmap.score_projection.after_high}</p>
                      <p className="text-[10px] text-gray-500">Fase 2</p>
                    </div>
                  </>
                )}
                <ChevronRight className="h-5 w-5 text-gray-400" />
                <div className="text-center">
                  <p className="text-2xl font-black text-emerald-600">{roadmap.score_projection.after_all}</p>
                  <p className="text-[10px] text-gray-500">Completado</p>
                </div>
              </div>
            </div>
          )}

          {/* Attack surface summary */}
          {roadmap.attack_surface_summary && (
            <div className="mb-6 grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[
                { key: 'exposed_services', label: 'Servicios Expuestos', icon: Radio, color: 'text-red-600' },
                { key: 'missing_protections', label: 'Protecciones Faltantes', icon: Shield, color: 'text-orange-600' },
                { key: 'dns_misconfigurations', label: 'DNS / Email', icon: Mail, color: 'text-yellow-600' },
                { key: 'web_vulnerabilities', label: 'Vulnerabilidades Web', icon: Bug, color: 'text-purple-600' },
                { key: 'ssl_issues', label: 'Problemas SSL', icon: Lock, color: 'text-blue-600' },
                { key: 'subdomains_at_risk', label: 'Subdominios Riesgo', icon: Network, color: 'text-pink-600' },
              ].map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="text-center p-2.5 rounded-xl bg-gray-50 border border-gray-200">
                  <Icon className={`h-4 w-4 mx-auto ${color}`} />
                  <p className="text-xl font-bold text-gray-900 mt-1">{roadmap.attack_surface_summary[key] ?? 0}</p>
                  <p className="text-[10px] text-gray-500 leading-tight">{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Phases */}
          <div className="space-y-4">
            {roadmap.phases.map((phase: any) => {
              const priorityColors: Record<string, string> = {
                URGENTE: 'bg-red-100 text-red-800 border-red-200',
                ALTA: 'bg-orange-100 text-orange-800 border-orange-200',
                MEDIA: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                MANTENIMIENTO: 'bg-emerald-100 text-emerald-800 border-emerald-200',
              };
              const phaseKey = `phase_${phase.phase}`;
              const isExpanded = expandedSections[phaseKey] ?? (phase.priority === 'URGENTE' || phase.priority === 'ALTA');

              return (
                <div key={phase.phase} className="rounded-xl border border-gray-200 overflow-hidden">
                  <button onClick={() => toggleSection(phaseKey)} className={`w-full px-4 py-3 border-b ${priorityColors[phase.priority] || 'bg-gray-50 border-gray-200'} flex items-center justify-between`}>
                    <div className="flex items-center gap-2 text-left">
                      <span className="font-black text-sm">Fase {phase.phase}:</span>
                      <span className="font-semibold text-sm">{phase.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs flex-shrink-0">
                      <span className="font-medium">{phase.timeframe}</span>
                      <span className="px-2 py-0.5 rounded-full bg-white/50 font-bold">{phase.priority}</span>
                      <span className="badge bg-white/60 text-gray-700">{phase.actions.length} acciones</span>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="p-4">
                      {phase.description && (
                        <p className="text-sm text-gray-600 mb-3 italic">{phase.description}</p>
                      )}
                      <div className="space-y-2">
                        {phase.actions.slice(0, 15).map((action: any, idx: number) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                              action.severity === 'critical' ? 'bg-red-500' :
                              action.severity === 'high' ? 'bg-orange-500' :
                              action.severity === 'medium' ? 'bg-yellow-500' : 'bg-emerald-500'
                            }`} />
                            <div className="flex-1">
                              <p className="font-medium text-gray-800">{action.title}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{action.description}</p>
                            </div>
                          </div>
                        ))}
                        {phase.actions.length > 15 && (
                          <p className="text-xs text-gray-400 ml-4 italic">...y {phase.actions.length - 15} acciones adicionales</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ========== 8. DETAILED FINDINGS (by severity group) — Internal only ========== */}
      <div data-section="internal-only">
      <div className="print-page-break-before" />
      <FindingsSection
        title="Hallazgos Criticos"
        severity="critical"
        findings={allFindings.filter((f: any) => f.severity === 'critical')}
        isExpanded={expandedSections.findings_critical}
        onToggle={() => toggleSection('findings_critical')}
      />
      <FindingsSection
        title="Hallazgos de Severidad Alta"
        severity="high"
        findings={allFindings.filter((f: any) => f.severity === 'high')}
        isExpanded={expandedSections.findings_high}
        onToggle={() => toggleSection('findings_high')}
      />
      <FindingsSection
        title="Hallazgos de Severidad Media"
        severity="medium"
        findings={allFindings.filter((f: any) => f.severity === 'medium')}
        isExpanded={expandedSections.findings_medium}
        onToggle={() => toggleSection('findings_medium')}
      />
      <FindingsSection
        title="Hallazgos de Severidad Baja"
        severity="low"
        findings={allFindings.filter((f: any) => f.severity === 'low')}
        isExpanded={expandedSections.findings_low}
        onToggle={() => toggleSection('findings_low')}
      />
      <FindingsSection
        title="Hallazgos Informativos"
        severity="info"
        findings={allFindings.filter((f: any) => f.severity === 'info')}
        isExpanded={expandedSections.findings_info}
        onToggle={() => toggleSection('findings_info')}
      />

      </div>{/* end internal-only findings wrapper */}

      {/* ========== 9. METHODOLOGY ========== */}
      <div className="print-page-break-before" />
      {methodology && (
        <div className="card">
          <button onClick={() => toggleSection('methodology')} className="w-full flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Terminal className="h-5 w-5 text-purple-600" />
              Metodologia de Escaneo ({methodology.total_modules} modulos)
            </h3>
            {expandedSections.methodology ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>

          {expandedSections.methodology && (
            <div className="mt-4">
              <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Tipo:</strong> {methodology.scan_type}
                  {methodology.environment?.deep && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      <Server className="h-3 w-3" /> EC2 Deep Scan
                    </span>
                  )}
                </p>
                <p className="text-xs text-blue-600 mt-1">{methodology.disclaimer}</p>
              </div>

              <div className="space-y-3">
                {methodology.modules_executed?.map((mod: any, i: number) => {
                  const IconComp = MODULE_ICONS[mod.module] || Terminal;
                  return (
                    <div key={i} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 flex-shrink-0">
                          <IconComp className="h-3.5 w-3.5 text-purple-700" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-gray-900 text-sm">{mod.name}</h4>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{mod.type}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">{mod.description}</p>
                          <p className="text-xs text-gray-400 mt-1 font-mono">Verificar: {mod.verification}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== 10. SERVICE RECOMMENDATIONS (CTA) — Internal only ========== */}
      <div data-section="internal-only">
      {recommendations.length > 0 && (
        <div className="card border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
          <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-purple-600" />
            Servicios Profesionales Recomendados
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Con base en los hallazgos identificados, estos son los servicios que su organizacion necesita para cerrar las brechas de seguridad:
          </p>

          <div className="space-y-4">
            {recommendations.map((rec: any, index: number) => (
              <div key={index} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-700 flex-shrink-0">
                    {rec.priority}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                      <span className={`badge ring-1 ${
                        rec.service_type === 'remediation' ? 'bg-red-100 text-red-700 ring-red-200' :
                        rec.service_type === 'managed_service' ? 'bg-purple-100 text-purple-700 ring-purple-200' :
                        'bg-yellow-100 text-yellow-700 ring-yellow-200'
                      }`}>
                        {rec.service_type === 'remediation' ? 'Remediacion' :
                         rec.service_type === 'managed_service' ? 'Servicio Gestionado' : 'Consultoria'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{rec.description}</p>
                    <div className="mt-3 flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Wrench className="h-3 w-3" /> Esfuerzo: {
                          rec.estimated_effort === 'low' ? 'Bajo' :
                          rec.estimated_effort === 'medium' ? 'Medio' : 'Alto'
                        }
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="h-3 w-3" /> Urgencia: {
                          rec.urgency === 'immediate' ? 'Inmediata' :
                          rec.urgency === 'short_term' ? 'Corto plazo' :
                          rec.urgency === 'medium_term' ? 'Mediano plazo' : 'Continuo'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-6 text-center rounded-xl border border-purple-300 bg-gradient-to-r from-purple-50 to-orange-50 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/20 mx-auto mb-3">
              <Shield className="h-6 w-6" />
            </div>
            <h4 className="text-lg font-bold text-gray-900">Necesita ayuda con la remediacion?</h4>
            <p className="mt-2 text-sm text-gray-600 max-w-lg mx-auto">
              Nuestro equipo de expertos en ciberseguridad puede ayudarle a remediar las vulnerabilidades encontradas
              e implementar un programa de seguridad integral para su organizacion.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://wa.me/573165650842?text=Hola%2C%20necesito%20ayuda%20con%20la%20remediacion%20de%20vulnerabilidades%20detectadas%20por%20xFreeScan"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                WhatsApp +57 316 565 0842
                <ChevronRight className="h-4 w-4" />
              </a>
              <a
                href="mailto:info@xclos.com?subject=Solicitud de servicios de remediacion - xFreeScan"
                className="btn-secondary"
              >
                info@xclos.com
              </a>
            </div>
            <p className="mt-3 text-xs text-gray-400">
              XCloud Solutions - Soluciones Cloud y Ciberseguridad Empresarial | www.xclos.com
            </p>
          </div>
        </div>
      )}

      </div>{/* end internal-only recommendations wrapper */}

      {/* AURA Chat Widget */}
      <AuraChat scanId={scanId || ''} />

      {/* ========== FOOTER ========== */}
      <div className="text-center text-xs text-gray-400 pb-8 print:pb-2">
        <p>Informe generado automaticamente por xFreeScan v3.0 de XCloud Solutions</p>
        <p>Todos los hallazgos se basan en evidencia verificable obtenida al momento del escaneo.</p>
        <p className="mt-1">WhatsApp: +57 316 565 0842 | Email: info@xclos.com | Web: www.xclos.com</p>
        <p className="mt-1 font-bold text-gray-500">CONFIDENCIAL — Uso exclusivo del destinatario</p>
      </div>

      {/* Print-only running footer */}
      <div className="print-footer hidden print:block">
        <p>xFreeScan v3.0 — XCloud Solutions | CONFIDENCIAL | {report.scan?.target || ''} | {new Date(report.generated_at).toLocaleDateString('es-CO')}</p>
      </div>

      {/* Video Report Modal */}
      {showVideo && (
        <VideoReport
          target={report.scan?.target || report.title?.replace('Informe de Seguridad - ', '') || ''}
          summary={summary}
          findings={allFindings}
          businessImpact={businessImpact}
          nistMaturity={nistMaturity}
          cyberRisk={cyberRisk}
          benchmark={benchmark}
          topPriorities={topPriorities}
          attackChains={attackChains}
          onClose={() => setShowVideo(false)}
        />
      )}
    </div>
  );
}

// ---- Sub-components ----

function ScoreCard({ label, value, suffix, color, highlight }: { label: string; value: any; suffix?: string; color: string; highlight?: boolean }) {
  return (
    <div className={`text-center p-3 rounded-xl border ${highlight ? 'border-red-300 bg-red-50 animate-pulse' : 'border-gray-200 bg-gray-50'}`}>
      <p className={`text-2xl font-black ${color}`}>
        {value}{suffix && <span className="text-sm font-medium text-gray-400">{suffix}</span>}
      </p>
      <p className="text-[10px] text-gray-500 mt-1 font-medium uppercase">{label}</p>
    </div>
  );
}

function FindingsSection({ title, severity, findings, isExpanded, onToggle }: {
  title: string;
  severity: string;
  findings: any[];
  isExpanded: boolean;
  onToggle: () => void;
}) {
  if (findings.length === 0) return null;

  const config = SEV[severity as keyof typeof SEV] || SEV.info;

  return (
    <div className="card">
      <button onClick={onToggle} className="w-full flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <span className={`h-3 w-3 rounded-full ${config.dot}`} />
          {title} ({findings.length})
        </h3>
        <div className="flex items-center gap-2">
          <span className={`badge ring-1 ${config.badge}`}>{config.label}</span>
          {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {findings.map((finding: any, index: number) => {
            const ModIcon = MODULE_ICONS[finding.scan_type] || Terminal;
            return (
              <div key={finding.id || index} className={`rounded-xl border p-4 print-finding ${config.light}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/80 flex-shrink-0">
                    <ModIcon className="h-3.5 w-3.5 text-gray-600" />
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-gray-900">{finding.title}</h4>
                      <ValidationBadge level={finding.validation_level} />
                      <span className="text-xs text-gray-500 bg-white/60 px-2 py-0.5 rounded-full">{finding.scan_type}</span>
                      {finding.cvss_v4?.score > 0 && (
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full font-mono">
                          CVSS {finding.cvss_v4.score}
                        </span>
                      )}
                      {finding.validation_level && finding.validation_level !== 'detected' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${finding.validation_level === 'exploited' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
                          {finding.validation_level === 'exploited' ? 'EXPLOTADO' : 'VALIDADO'}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-700">{finding.description}</p>

                    {/* Evidence */}
                    {finding.evidence && (
                      <div className="mt-2 rounded-lg bg-white/70 border border-white/80 p-2.5">
                        <p className="text-xs font-semibold uppercase text-gray-500 mb-1 flex items-center gap-1">
                          <Eye className="h-3 w-3" /> Evidencia
                        </p>
                        <p className="text-xs text-gray-600 font-mono whitespace-pre-wrap break-all">{finding.evidence}</p>
                      </div>
                    )}

                    {/* Compliance mapping */}
                    {finding.compliance && Object.keys(finding.compliance).length > 0 && (
                      <div className="mt-2 rounded-lg bg-purple-50/70 border border-purple-200/60 p-2.5">
                        <p className="text-xs font-semibold uppercase text-purple-600 mb-1">Controles de cumplimiento afectados</p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(finding.compliance).map(([fw, ctrls]: [string, any]) =>
                            (ctrls as string[]).slice(0, 3).map((ctrl: string, ci: number) => (
                              <span key={`${fw}-${ci}`} className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                {fw}: {ctrl}
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {/* References */}
                    {finding.references?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {finding.references.map((ref: string, ri: number) => {
                          try {
                            return (
                              <a key={ri} href={ref} target="_blank" rel="noopener noreferrer"
                                 className="no-print-url text-[10px] text-purple-600 hover:text-purple-800 underline flex items-center gap-0.5">
                                <ExternalLink className="h-2.5 w-2.5" />
                                {new URL(ref).hostname}
                              </a>
                            );
                          } catch {
                            return <span key={ri} className="text-[10px] text-gray-500">{ref}</span>;
                          }
                        })}
                      </div>
                    )}

                    {/* Remediation */}
                    {finding.remediation && (
                      <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                        <p className="text-xs font-semibold uppercase text-emerald-700 mb-1">Remediacion recomendada</p>
                        <p className="text-sm text-emerald-800">{finding.remediation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upgrade modal — disabled temporarily */}
    </div>
  );
}
